import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, HeartPulse, Loader2, RefreshCw } from "lucide-react";
import { operasyonHub } from "@/lib/api";

function SkorBadge({ skor }) {
    const tone =
        skor >= 80 ? "bg-emerald-500/20 text-emerald-300" : skor >= 55 ? "bg-amber-500/20 text-amber-300" : "bg-rose-500/20 text-rose-300";
    return (
        <span className={`inline-flex h-10 w-10 items-center justify-center rounded-full font-bold tabular-nums ${tone}`}>
            {skor}
        </span>
    );
}

export default function DosyaSaglikPanosuPage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [onlyKritik, setOnlyKritik] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            setData(
                await operasyonHub.saglik({
                    kritik: onlyKritik ? "1" : "0",
                    limit: 60,
                }),
            );
        } catch {
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [onlyKritik]);

    useEffect(() => {
        load();
    }, [load]);

    return (
        <div className="max-w-screen-2xl mx-auto space-y-6 pb-16 px-4">
            <header className="flex flex-wrap items-center justify-between gap-4">
                <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]">Operasyon Kalitesi</p>
                    <h1 className="font-display text-3xl text-white mt-1">Dosya Sağlık Panosu</h1>
                    <p className="text-sm text-slate-500 mt-1 max-w-xl">
                        0–100 skor: evrak %40, değer kaybı %25, süreç %20, ödeme %15. Düşük skorlu dosyaları önceliklendirin.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <label className="flex items-center gap-2 text-xs text-slate-400">
                        <input
                            type="checkbox"
                            checked={onlyKritik}
                            onChange={(e) => setOnlyKritik(e.target.checked)}
                            className="rounded"
                        />
                        Sadece kritik (&lt;60)
                    </label>
                    <button
                        type="button"
                        onClick={load}
                        disabled={loading}
                        className="h-10 px-4 rounded-xl bg-[#D4AF37] text-slate-900 text-sm font-bold inline-flex items-center gap-2"
                    >
                        <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                        Yenile
                    </button>
                </div>
            </header>

            {data?.ozet ? (
                <div className="grid grid-cols-3 gap-3 max-w-md">
                    <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-3 text-center">
                        <p className="text-2xl font-bold text-rose-300">{data.ozet.kritik}</p>
                        <p className="text-[10px] uppercase text-slate-500">Kritik</p>
                    </div>
                    <div className="rounded-xl border border-amber-500/30 p-3 text-center">
                        <p className="text-2xl font-bold text-amber-300">{data.ozet.orta}</p>
                        <p className="text-[10px] uppercase text-slate-500">Orta</p>
                    </div>
                    <div className="rounded-xl border border-emerald-500/30 p-3 text-center">
                        <p className="text-2xl font-bold text-emerald-300">{data.ozet.iyi}</p>
                        <p className="text-[10px] uppercase text-slate-500">İyi</p>
                    </div>
                </div>
            ) : null}

            {loading ? (
                <div className="flex justify-center py-20">
                    <Loader2 className="h-8 w-8 animate-spin text-[#D4AF37]" />
                </div>
            ) : (
                <div className="space-y-2">
                    {(data?.dosyalar || []).map((d) => (
                        <div
                            key={d.sigorta_dosyasi_id || d.dosya_no}
                            className="rounded-xl border border-white/[0.08] bg-[#0a1018]/60 px-4 py-3 flex flex-wrap items-start gap-4"
                        >
                            <SkorBadge skor={d.skor} />
                            <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                    <Link
                                        to={`/e/sigorta-dosyalari/${d.sigorta_dosyasi_id}`}
                                        className="font-mono text-sm text-[#D4AF37] hover:underline"
                                    >
                                        {d.dosya_no}
                                    </Link>
                                    <span className="text-xs text-slate-500">{d.plaka}</span>
                                    <span className="text-[10px] uppercase text-slate-600">{d.durum}</span>
                                </div>
                                <div className="flex flex-wrap gap-3 mt-2 text-[10px] text-slate-500">
                                    <span>Evrak {d.evrak_puan}/40</span>
                                    <span>DK {d.deger_kaybi_puan}/25</span>
                                    <span>Süreç {d.surec_puan}/20</span>
                                    <span>Ödeme {d.odeme_puan}/15</span>
                                </div>
                                {d.oneriler?.length ? (
                                    <ul className="mt-2 text-xs text-amber-300/90 space-y-0.5">
                                        {d.oneriler.slice(0, 3).map((o, i) => (
                                            <li key={i} className="flex items-start gap-1">
                                                <AlertTriangle className="h-3 w-3 shrink-0 mt-0.5" />
                                                {o}
                                            </li>
                                        ))}
                                    </ul>
                                ) : null}
                            </div>
                        </div>
                    ))}
                    {!data?.dosyalar?.length ? (
                        <p className="text-center text-slate-500 py-12 flex flex-col items-center gap-2">
                            <HeartPulse className="h-10 w-10 opacity-30" />
                            Kritik dosya yok — operasyon sağlıklı
                        </p>
                    ) : null}
                </div>
            )}
        </div>
    );
}
